import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SelectOption } from "@idw-types/common";
import { navigateToUtil } from "@utils/navigation-util";
import { MANAGEMENT_CONSOLE_AXN_ADMIN_SECURITY_RULES_URL } from "@constants/routes";
import { InputFormGroup } from "../../../common/input-form-group/input-form-group";
import { TextareaFormGroup } from "../../../common/textarea-form-group/textarea-form-group";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { ButtonWithSpinner } from "../../../common/button-with-spinner/button-with-spinner";
import { resources } from "@constants/hardcoded-strings";
import { useDispatch } from "react-redux";
import { AxnAdminSecurityRule } from "@idw-types/data/management-console/axnadmin";
import debounce from "lodash/debounce";
import {
    SecurityRuleStatusSelectFormGroup,
    SecurityRuleOperationTypeSelectFormGroup
} from "../../../common/select-form-groups";
import { updateSecurityRuleReduxRequest } from "@redux-requests/security-rule-details";
import styles from "./security-rule-details-component.less";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";

type SecurityRuleDetailsComponentProps = {
    securityRule: AxnAdminSecurityRule;
    securityRules: Array<AxnAdminSecurityRule>;
    isDisabled: boolean;
    setIsDisabled: (isDisabled: boolean) => void;
};

type SecurityRuleDetailsComponentState = {
    securityRule: AxnAdminSecurityRule;
    selectedOperationTypeOption: SelectOption;
    selectedStatusOption: SelectOption;
};


export const SecurityRuleDetailsComponent = (props: SecurityRuleDetailsComponentProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { securityRule, securityRules, isDisabled, setIsDisabled } = props;
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [state, setState] = useState<SecurityRuleDetailsComponentState>({
        securityRule: securityRule,
        selectedOperationTypeOption: securityRule.operationType ? {
            label: securityRule.operationType,
            value: securityRule.operationType
        } : null,
        selectedStatusOption: securityRule.status ? {
            label: securityRule.status,
            value: securityRule.status
        } : null
    });

    const [idCheckMessage, setIdCheckMessage] = useState("");

    const checkSecurityRuleId = debounce(() => {
        if (
            (securityRule && securityRule.id !== state.securityRule.id && state.securityRule.id !== "") ||
            !securityRule && state.securityRule.id !== ""
        ) {
            const existingRule = securityRules.find(sr => sr.id === state.securityRule.id);
            if (existingRule) {
                setIdCheckMessage(resources.pages.securityRuleDetails.idAlreadyExistsMessage);
            } else {
                setIdCheckMessage(resources.pages.securityRuleDetails.idIsAvailableMessage);
            }
        } else {
            setIdCheckMessage("");
        }
    }, 300);

    useEffect(() => {
        setIdCheckMessage(resources.pages.securityRuleDetails.checking);
        if (state.securityRule.id !== "") {
            checkSecurityRuleId();
        } else {
            setIdCheckMessage("");
        }
    }, [state.securityRule.id]);

    const handleUpdateId = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            securityRule: {
                ...state.securityRule,
                id: event.target.value
            }
        });
    };

    const handleUpdateClassName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            securityRule: {
                ...state.securityRule,
                className: event.target.value
            }
        });
    };

    const handleUpdatePathText = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            securityRule: {
                ...state.securityRule,
                pathText: event.target.value
            }
        });
    };

    const handleUpdateSecurityRuleOperationType = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                securityRule: {
                    ...state.securityRule,
                    operationType: newValue.value,
                },
                selectedOperationTypeOption: newValue
            });
        }
    };

    const handleUpdateSecurityRuleStatus = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                securityRule: {
                    ...state.securityRule,
                    status: newValue.value
                },
                selectedStatusOption: newValue
            });
        }
    };

    const handleUpdateRuleName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            securityRule: {
                ...state.securityRule,
                ruleName: event.target.value
            }
        });
    };

    const handleUpdateRule = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            securityRule: {
                ...state.securityRule,
                rule: event.target.value
            }
        });
    };

    const onCloseClick = () => {
        navigateToUtil(MANAGEMENT_CONSOLE_AXN_ADMIN_SECURITY_RULES_URL, navigate, routerLocation.pathname);
    };

    const onEditClick = () => {
        setIsDisabled(false);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);
        const updateSecurityRuleResult = await updateSecurityRuleReduxRequest(dispatch)(securityRule, state.securityRule);
        if (updateSecurityRuleResult) {
            setState({
                securityRule: updateSecurityRuleResult,
                selectedOperationTypeOption: updateSecurityRuleResult.operationType ? {
                    label: updateSecurityRuleResult.operationType,
                    value: updateSecurityRuleResult.operationType
                } : null,
                selectedStatusOption: updateSecurityRuleResult.status ? {
                    label: updateSecurityRuleResult.status,
                    value: updateSecurityRuleResult.status
                } : null
            });
        }
        setRequestInProgress(false);
        setIsDisabled(true);
    };

    const onCancelClick = () => {
        setState({
            securityRule: securityRule,
            selectedOperationTypeOption: securityRule.operationType ? {
                label: securityRule.operationType,
                value: securityRule.operationType
            } : null,
            selectedStatusOption: securityRule.status ? {
                label: securityRule.status,
                value: securityRule.status
            } : null
        });
        setIsDisabled(true);
    };

    return (
        <div className={styles.securityRuleDetailsComponent}>
            <Form onSubmit={handleSubmit}>
                <InputFormGroup
                    controlId="security-rule-id-input"
                    label={resources.pages.securityRuleDetails.inputs.id.label}
                    ariaLabel={resources.pages.securityRuleDetails.inputs.id.label}
                    automationId="security-rule-id-input"
                    placeholder={resources.pages.securityRuleDetails.inputs.id.label}
                    value={state.securityRule.id}
                    onChange={handleUpdateId}
                    disabled={isDisabled}
                    isRequired={true}
                    labelWidth={labelWidth}
                />
                {idCheckMessage && !isDisabled && (
                    <div className={styles.idCheckMessage}>{idCheckMessage}</div>
                )}
                <InputFormGroup
                    controlId="security-rule-class-name-input"
                    label={resources.pages.securityRuleDetails.inputs.className.label}
                    ariaLabel={resources.pages.securityRuleDetails.inputs.className.label}
                    automationId="security-rule-class-name-input"
                    placeholder={resources.pages.securityRuleDetails.inputs.className.label}
                    value={state.securityRule.className || ""}
                    onChange={handleUpdateClassName}
                    disabled={isDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                />
                <InputFormGroup
                    controlId="security-rule-path-text-input"
                    label={resources.pages.securityRuleDetails.inputs.pathText.label}
                    ariaLabel={resources.pages.securityRuleDetails.inputs.pathText.label}
                    automationId="security-rule-path-text-input"
                    placeholder={resources.pages.securityRuleDetails.inputs.pathText.label}
                    value={state.securityRule.pathText || ""}
                    onChange={handleUpdatePathText}
                    disabled={isDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                />
                <SecurityRuleOperationTypeSelectFormGroup
                    idPrefix="management-console"
                    onChange={handleUpdateSecurityRuleOperationType}
                    selectedOption={state.selectedOperationTypeOption}
                    disabled={isDisabled}
                    isRequired={true}
                    isSearchable={false}
                    isClearable={false}
                    labelWidth={labelWidth}
                />
                <SecurityRuleStatusSelectFormGroup
                    idPrefix="management-console"
                    onChange={handleUpdateSecurityRuleStatus}
                    selectedOption={state.selectedStatusOption}
                    disabled={isDisabled}
                    isRequired={true}
                    isSearchable={false}
                    isClearable={false}
                    labelWidth={labelWidth}
                />
                <InputFormGroup
                    controlId="security-rule-rule-name-input"
                    label={resources.pages.securityRuleDetails.inputs.ruleName.label}
                    ariaLabel={resources.pages.securityRuleDetails.inputs.ruleName.label}
                    automationId="security-rule-rule-name-input"
                    placeholder={resources.pages.securityRuleDetails.inputs.ruleName.label}
                    value={state.securityRule.ruleName || ""}
                    onChange={handleUpdateRuleName}
                    disabled={isDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                />
                <TextareaFormGroup
                    controlId="security-rule-rule-input"
                    label={resources.pages.securityRuleDetails.inputs.rule.label}
                    ariaLabel={resources.pages.securityRuleDetails.inputs.rule.label}
                    automationId="security-rule-rule-input"
                    placeholder={resources.pages.securityRuleDetails.inputs.rule.label}
                    value={state.securityRule.rule || ""}
                    onChange={handleUpdateRule}
                    disabled={isDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                />
                <div className={styles.buttonDiv}>
                    <Button onClick={onCloseClick} variant={"outline-secondary"}>{resources.common.buttons.close.label}</Button>
                    {isDisabled && (
                        <Button onClick={onEditClick}>{resources.common.buttons.edit.label}</Button>
                    )}
                    {!isDisabled && (
                        <>
                            <Button id="security-rule-detail-cancel-button" onClick={onCancelClick} variant={"outline-secondary"}>{resources.common.buttons.cancel.label}</Button>
                            <ButtonWithSpinner
                                id="security-rule-detail-save-button"
                                disabled={requestInProgress}
                                hideSpinner={false}
                                variant="primary"
                                type={ButtonTypes.SUBMIT}
                                label={resources.common.buttons.save.label}
                            />
                        </>
                    )}
                </div>
            </Form>
        </div>
    );
};